/**
 * Recording a vehicle going back to the seller it was bought from.
 *
 * Winnie, 18 September. Offered from the Cancelled action rather than as a status of its own,
 * because she is explicit that inventing a `returned_to_seller` status "would recreate exactly
 * the duplication problem the registry exists to prevent."
 *
 * The screen is deliberately blunt about what pressing the button does. Four things happen at
 * once — the cost lines are voided, the PPSR registration is released, a GST credit reversal
 * may post, and the vehicle is cancelled — and three of those are invisible from here.
 */
import { useState } from 'react'
import { Deal, User } from '../../types'
import {
  ISSUE_LABEL,
  ISSUE_TYPES,
  ReturnIssueType,
  returnVehicleToSeller,
} from '../../services/vehicleReturnService'
import { toast } from '../Toast'
import './SaleGovernancePanels.css'

export default function VehicleReturnDialog({
  deal,
  user,
  onClose,
  onDone,
}: {
  deal: Deal
  user: User
  onClose: () => void
  onDone: () => void
}) {
  const [issueType, setIssueType] = useState<ReturnIssueType>('OdometerDiscrepancy')
  const [description, setDescription] = useState('')
  const [refund, setRefund] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setNote(null)
    try {
      const res = await returnVehicleToSeller({
        dealId: deal.id,
        issueType,
        description: description.trim(),
        refundAmountExpected: refund.trim() ? Number(refund) : null,
        user,
      })
      setNote(res.message)
      if (res.ok) {
        toast.saved()
        onDone()
      } else {
        toast.error(res.message)
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'The return could not be recorded.'
      setNote(msg)
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ws-confirm" role="alertdialog">
      <div className="ws-confirm-card" style={{ maxWidth: 560 }}>
        <h4>Return this vehicle to the seller</h4>
        <p className="sgp-hint">
          For a material defect found after acquisition that was not disclosed at purchase. This is not
          the path for a problem found after the vehicle has been sold on — that is a dispute with the
          wholesale buyer, and this will refuse it.
        </p>

        <label className="sgp-field wide">
          <span>Issue</span>
          <select value={issueType} onChange={(e) => setIssueType(e.target.value as ReturnIssueType)}>
            {ISSUE_TYPES.map((t) => (
              <option key={t} value={t}>
                {ISSUE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>

        <label className="sgp-field wide">
          <span>What was found — required</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. odometer reads 84,000km; service history shows 187,000km in March"
          />
        </label>

        <label className="sgp-field wide">
          <span>Refund expected from the seller</span>
          <input type="number" step="0.01" value={refund} onChange={(e) => setRefund(e.target.value)} />
        </label>

        <p className="sgp-warn">
          Recording this cancels the vehicle, voids every cost line against it (retained, not deleted),
          releases any PPSR registration Direct Auto lodged, and — where the vehicle was bought on a tax
          invoice — posts a GST credit reversal into the current period as its own line on the Margin
          Scheme register. The original purchase-period claim is left exactly as it was.
        </p>

        {note && <div className="sgp-note">{note}</div>}

        <div className="ws-confirm-actions">
          <button className="ws-advance-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="ws-advance-btn danger" onClick={save} disabled={busy || !description.trim()}>
            {busy ? 'Recording…' : 'Record the return'}
          </button>
        </div>
      </div>
    </div>
  )
}
